import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilmCard from "@/components/feature/FilmCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getFilmsByType, getAllLanguages, getAllRegions, getAllYears } from "@/utils/film";
import type { Film } from "@/types/content";

type SortKey = "latest" | "hot";

const PAGE_SIZE = 9;

function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

interface FilterGroupProps {
  title: string;
  options: (string | number)[];
  selected: string[];
  onToggle: (value: string) => void;
}

function FilterGroup({ title, options, selected, onToggle }: FilterGroupProps) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-4 last:border-b-0 sm:flex-row sm:items-start sm:gap-4">
      <span className="w-16 shrink-0 pt-1 text-sm font-medium text-foreground-500">{title}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const value = String(option);
          const active = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => onToggle(value)}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { type = "电影" } = useParams();
  const decodedType = decodeURIComponent(type);

  useDocumentMeta(
    `妖精网站 - ${decodedType}合集与推荐`,
    `妖精网站提供丰富的${decodedType}妖精影视资源，包括最新电影和电视剧，满足观众需求。`
  );

  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("latest");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setRegions([]);
    setYears([]);
    setLanguages([]);
    setSort("latest");
    setVisibleCount(PAGE_SIZE);
  }, [decodedType]);

  const allRegions = useMemo(() => getAllRegions(), []);
  const allYears = useMemo(() => getAllYears(), []);
  const allLanguages = useMemo(() => getAllLanguages(), []);

  const filtered = useMemo(() => {
    let result: Film[] = getFilmsByType(decodedType);
    if (regions.length) result = result.filter((film) => regions.includes(film.region));
    if (years.length) result = result.filter((film) => years.includes(String(film.year)));
    if (languages.length) result = result.filter((film) => languages.includes(film.language));
    result = [...result].sort((a, b) =>
      sort === "latest" ? b.year - a.year : b.popularity - a.popularity
    );
    return result;
  }, [decodedType, regions, years, languages, sort]);

  const visibleFilms = filtered.slice(0, visibleCount);
  const hasFilters = regions.length > 0 || years.length > 0 || languages.length > 0;

  const clearFilters = () => {
    setRegions([]);
    setYears([]);
    setLanguages([]);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">妖精{decodedType}</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          妖精{decodedType}大全
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          汇集妖精题材{decodedType}作品，支持地区、年代、语言多维筛选与排序，帮你快速找到心仪资源。
        </p>
      </div>

      <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="flex items-center gap-2 text-sm font-semibold text-foreground-900 sm:cursor-default"
          >
            <i className="ri-filter-3-line text-primary-600 sm:hidden" />
            筛选条件
            {hasFilters && (
              <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-700">
                已选 {regions.length + years.length + languages.length} 项
              </span>
            )}
            <i
              className={`ri-arrow-down-s-line transition-transform sm:hidden ${
                filtersOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-foreground-500 sm:block">排序</span>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-100 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
            >
              <option value="latest">最新发布</option>
              <option value="hot">热门程度</option>
            </select>
          </div>
        </div>

        <div
          className={`mt-2 sm:block ${filtersOpen ? "block" : "hidden"}`}
        >
          <FilterGroup
            title="地区"
            options={allRegions}
            selected={regions}
            onToggle={(value) => {
              setRegions((prev) => toggleValue(prev, value));
              setVisibleCount(PAGE_SIZE);
            }}
          />
          <FilterGroup
            title="年代"
            options={allYears}
            selected={years}
            onToggle={(value) => {
              setYears((prev) => toggleValue(prev, value));
              setVisibleCount(PAGE_SIZE);
            }}
          />
          <FilterGroup
            title="语言"
            options={allLanguages}
            selected={languages}
            onToggle={(value) => {
              setLanguages((prev) => toggleValue(prev, value));
              setVisibleCount(PAGE_SIZE);
            }}
          />
          {hasFilters && (
            <div className="pt-4">
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                <i className="ri-delete-bin-6-line" />
                清空筛选
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mb-4 text-sm text-foreground-500">
        共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部妖精
        {decodedType}作品
      </p>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-800">
            无符合条件的妖精影视作品
          </p>
          <p className="mt-2 text-sm text-foreground-500">试着调整或清空筛选条件再来看看</p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            清空筛选
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-3 xl:grid-cols-3">
            {visibleFilms.map((film) => (
              <FilmCard key={film.id} film={film} showPopularity showIntro />
            ))}
          </div>
          {visibleCount < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50"
              >
                <i className="ri-add-line" />
                加载更多（剩余 {filtered.length - visibleCount} 部）
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}